// Custom dropdown enhancement for native <select> elements.
//
// The native select stays in the DOM as the single source of truth —
// hidden, never removed. The visible UI is a trigger button plus a
// body-appended listbox; picking an option writes select.value and
// dispatches a bubbling 'change' event, so every existing listener
// (craft switcher, propulsion rebuild, graphics save, tuners) works
// unmodified. Programmatic writes (select.value = x, select.disabled
// = true, options rebuilt via innerHTML) are mirrored back to the
// trigger via a value-setter patch, an attribute observer, and
// fresh-read-on-open option building.
//
// Every device gets the custom menu, touch included: no native
// pickers anywhere in the viewer. Rows grow to 44px on coarse pointers.

const MENU_Z = 10500; // above the highest app layer (10000)

const STYLE = `
  .sd-hidden { display: none !important; }

  .sd-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    min-height: 32px;
    background: #141922;
    border: none;
    border-radius: 8px;
    color: #ece7da;
    font-family: 'Inter', sans-serif;
    font-size: 0.78rem;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    text-align: left;
    cursor: pointer;
    min-width: 128px;
    max-width: 210px;
    transition: background-color 0.18s ease;
  }
  .sd-trigger:hover,
  .sd-trigger[aria-expanded="true"] { background: #1b212c; }
  .sd-trigger:focus { outline: none; }
  .sd-trigger:focus-visible {
    outline: 2px solid #7dd3a8;
    outline-offset: 2px;
  }
  .sd-trigger:disabled { opacity: 0.45; cursor: default; }
  .sd-trigger-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sd-caret {
    flex: none;
    width: 6px;
    height: 6px;
    border-right: 1px solid currentColor;
    border-bottom: 1px solid currentColor;
    color: #a8a396;
    transform: rotate(45deg) translateY(-1px);
    transition: transform 0.18s ease;
  }
  .sd-trigger[aria-expanded="true"] .sd-caret {
    transform: rotate(-135deg) translateY(1px);
  }

  /* Context parity with the element-selector CSS the native selects
     had (.graphics-row select, tuner/admin panel selects). */
  .graphics-row .sd-trigger {
    color: #ece7da;
    font-size: 12px;
    letter-spacing: normal;
    text-transform: none;
    padding: 5px 8px;
    min-width: 190px;
    max-width: none;
  }
  .camera-tuner-field .sd-trigger,
  .craft-admin-row > .sd-trigger {
    width: 100%;
    max-width: none;
    min-width: 0;
    font-size: 12px;
    letter-spacing: normal;
    text-transform: none;
    padding: 4px 6px;
  }

  .sd-menu {
    position: fixed;
    z-index: ${MENU_Z};
    background: #0a0d13;
    border: none;
    border-radius: 10px;
    padding: 6px;
    max-height: min(46vh, 380px);
    overflow-y: auto;
    overscroll-behavior: contain;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.16s ease, transform 0.16s ease;
  }
  .sd-menu.sd-up { transform: translateY(4px); }
  .sd-menu.sd-open { opacity: 1; transform: none; }

  .sd-option {
    position: relative;
    padding: 8px 12px;
    border-radius: 6px;
    font-family: 'Inter', sans-serif;
    font-size: 0.8rem;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
    color: #a8a396;
    white-space: nowrap;
    cursor: pointer;
  }
  .sd-menu.sd-plain .sd-option {
    font-size: 12px;
    letter-spacing: normal;
    text-transform: none;
  }
  .sd-option.sd-active {
    background: #1b212c;
    color: #ece7da;
  }
  .sd-option[aria-selected="true"] { color: #ece7da; background: #15302a; }
  .sd-option[aria-selected="true"].sd-active { background: #1b3b33; }
  .sd-option[aria-disabled="true"] { opacity: 0.35; cursor: default; }

  /* Touch: finger-sized trigger and rows. */
  @media (pointer: coarse) {
    .sd-trigger { min-height: 44px; padding: 0 12px; }
    .sd-option { display: flex; align-items: center; min-height: 44px; padding: 0 14px; }
    .sd-menu { max-height: min(60vh, 440px); }
  }

  @media (prefers-reduced-motion: reduce) {
    .sd-trigger, .sd-caret, .sd-menu { transition: none; }
  }
`;

let styleInjected = false;
let activeMenu = null; // { select, trigger, menu, dispose }

function injectStyle() {
  if (styleInjected) return;
  styleInjected = true;
  const tag = document.createElement('style');
  tag.dataset.sdStyles = '1';
  tag.textContent = STYLE;
  document.head.appendChild(tag);
}

function selectedLabel(select) {
  const opt = select.options[select.selectedIndex];
  return opt ? opt.textContent.trim() : '';
}

function closeActiveMenu(returnFocus = false) {
  if (!activeMenu) return;
  const { trigger, menu, dispose } = activeMenu;
  activeMenu = null;
  dispose();
  trigger.setAttribute('aria-expanded', 'false');
  menu.classList.remove('sd-open');
  // Let the close transition play, then drop the node.
  const remove = () => menu.remove();
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) remove();
  else setTimeout(remove, 180);
  if (returnFocus) trigger.focus({ preventScroll: true });
}

function openMenuFor(select, trigger) {
  if (activeMenu && activeMenu.select === select) { closeActiveMenu(); return; }
  closeActiveMenu();

  const menu = document.createElement('div');
  menu.className = 'sd-menu';
  menu.setAttribute('role', 'listbox');
  if (trigger.closest('.graphics-row, .camera-tuner-field, .craft-admin-row')) {
    menu.classList.add('sd-plain');
  }

  // Fresh read every open — rebuilt option lists (propulsion per
  // craft) always render current without childList observers.
  const optionEls = [];
  let activeIndex = Math.max(0, select.selectedIndex);
  Array.from(select.options).forEach((opt, i) => {
    const el = document.createElement('div');
    el.className = 'sd-option';
    el.setAttribute('role', 'option');
    el.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
    if (opt.disabled) el.setAttribute('aria-disabled', 'true');
    el.textContent = opt.textContent.trim();
    el.addEventListener('pointerenter', () => setActive(i));
    el.addEventListener('click', () => { if (!opt.disabled) commit(i); });
    optionEls.push(el);
    menu.appendChild(el);
  });

  function setActive(i) {
    if (i < 0 || i >= optionEls.length) return;
    optionEls.forEach((el, n) => el.classList.toggle('sd-active', n === i));
    activeIndex = i;
    const el = optionEls[i];
    const mTop = menu.scrollTop;
    if (el.offsetTop < mTop) menu.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > mTop + menu.clientHeight) {
      menu.scrollTop = el.offsetTop + el.offsetHeight - menu.clientHeight;
    }
  }

  function commit(i) {
    const opt = select.options[i];
    closeActiveMenu(true);
    if (!opt || opt.value === select.value) return;
    select.value = opt.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Position: anchored to the trigger, flipped upward when the space
  // below is short (the fly HUD sits at the bottom edge).
  const rect = trigger.getBoundingClientRect();
  menu.style.minWidth = `${Math.ceil(rect.width)}px`;
  menu.style.visibility = 'hidden';
  document.body.appendChild(menu);
  const menuH = menu.offsetHeight;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp = spaceBelow < menuH + 12 && rect.top > spaceBelow;
  if (openUp) {
    menu.classList.add('sd-up');
    menu.style.top = `${Math.max(8, rect.top - menuH - 4)}px`;
  } else {
    menu.style.top = `${Math.min(rect.bottom + 4, window.innerHeight - menuH - 8)}px`;
  }
  menu.style.left = `${Math.min(rect.left, Math.max(8, window.innerWidth - menu.offsetWidth - 8))}px`;
  menu.style.visibility = '';
  requestAnimationFrame(() => menu.classList.add('sd-open'));
  setActive(activeIndex);

  // Type-ahead buffer.
  let typed = '';
  let typedAt = 0;

  function onKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); closeActiveMenu(true); return; }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); commit(activeIndex); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(activeIndex + 1, optionEls.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(activeIndex - 1, 0)); return; }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(optionEls.length - 1); return; }
    if (e.key === 'Tab') { closeActiveMenu(); return; }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = performance.now();
      typed = (now - typedAt < 500 ? typed : '') + e.key.toLowerCase();
      typedAt = now;
      const hit = Array.from(select.options).findIndex(
        (o) => o.textContent.trim().toLowerCase().startsWith(typed)
      );
      if (hit >= 0) setActive(hit);
    }
  }

  // The menu lives on document.body, outside the fly-HUD containers
  // the app's window-level wheel handler exempts — so without this,
  // scrolling the craft list also dollies the chase camera. Wheel
  // input over the menu belongs to the menu alone; scrolling still
  // works natively, the event just never reaches window.
  menu.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });

  function onPointerDown(e) {
    if (!menu.contains(e.target) && e.target !== trigger && !trigger.contains(e.target)) {
      closeActiveMenu();
    }
  }
  function onScroll(e) {
    if (!menu.contains(e.target)) closeActiveMenu();
  }
  function onResize() { closeActiveMenu(); }

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);

  activeMenu = {
    select,
    trigger,
    menu,
    dispose() {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    }
  };
  trigger.setAttribute('aria-expanded', 'true');
}

export function enhanceSelect(select) {
  if (!select || select.dataset.sdEnhanced) return;
  injectStyle();
  select.dataset.sdEnhanced = '1';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'sd-trigger';
  // Carry over class-based styling hooks (.propulsion-select etc.)
  // minus the enhancement marker semantics.
  select.classList.forEach((cls) => trigger.classList.add(cls));
  if (select.title) trigger.title = select.title;
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  const labelEl = document.createElement('span');
  labelEl.className = 'sd-trigger-label';
  const caret = document.createElement('span');
  caret.className = 'sd-caret';
  caret.setAttribute('aria-hidden', 'true');
  trigger.append(labelEl, caret);

  const syncLabel = () => { labelEl.textContent = selectedLabel(select); };
  const syncDisabled = () => { trigger.disabled = select.disabled; };
  syncLabel();
  syncDisabled();

  select.classList.add('sd-hidden');
  select.insertAdjacentElement('afterend', trigger);

  trigger.addEventListener('click', () => {
    if (trigger.disabled) return;
    openMenuFor(select, trigger);
  });
  trigger.addEventListener('keydown', (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && trigger.getAttribute('aria-expanded') === 'false') {
      e.preventDefault();
      openMenuFor(select, trigger);
    }
  });

  // External 'change' events (and our own commits) refresh the label.
  select.addEventListener('change', syncLabel);

  // Programmatic value writes don't fire events — patch the instance
  // accessor so `craftSelect.value = previousLabel` style reverts
  // reflect on the trigger immediately.
  const proto = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
  if (proto && proto.set) {
    Object.defineProperty(select, 'value', {
      get() { return proto.get.call(this); },
      set(v) { proto.set.call(this, v); syncLabel(); },
      configurable: true
    });
  }

  // disabled toggles + option rebuilds (innerHTML) reflected from
  // attribute / childList mutations.
  const observer = new MutationObserver(() => { syncDisabled(); syncLabel(); });
  observer.observe(select, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
}

export function enhanceAllSelects(root = document) {
  injectStyle();
  root.querySelectorAll('select').forEach(enhanceSelect);

  // Late-created selects (dev tuner panels and anything dynamic):
  // first pointer interaction enhances them in place and opens the
  // custom menu instead of the native popup.
  if (root === document && !document.__sdDelegated) {
    document.__sdDelegated = true;
    document.addEventListener('pointerdown', (e) => {
      const t = e.target;
      if (t && t.tagName === 'SELECT' && !t.dataset.sdEnhanced) {
        e.preventDefault();
        enhanceSelect(t);
        const trigger = t.nextElementSibling;
        if (trigger && trigger.classList.contains('sd-trigger')) {
          openMenuFor(t, trigger);
        }
      }
    }, true);
  }
}
